import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  Hammer,
  Lightbulb,
  Loader2,
  ShieldAlert,
  ShoppingCart,
  Share2,
  Sparkles,
} from 'lucide-react';
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { SelfBuildCostReport2026 } from '../../types/selfBuildCost2026';
import PseoEmailCapture from '../../components/pseo/PseoEmailCapture';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatMultiplier = (value: number) => `${(value || 0).toFixed(2)}x`;

const SelfBuildCostPage: React.FC = () => {
  const { selfBuildSlug } = useParams<{ selfBuildSlug: string }>();
  const [data, setData] = useState<SelfBuildCostReport2026 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeInsight, setActiveInsight] = useState<'savings' | 'procurement' | 'risks'>(
    'savings'
  );
  const [expandedInsights, setExpandedInsights] = useState({
    savings: false,
    procurement: false,
    risks: false,
  });

  const slug = useMemo(() => String(selfBuildSlug || '').trim().toLowerCase(), [selfBuildSlug]);
  const mergedFaq = useMemo(() => {
    const allFaq = [
      ...(data?.seo_content?.faq || []),
      ...(data?.ai_enrichment?.faq_additions || []),
    ];
    const seen = new Set<string>();
    return allFaq.filter((item) => {
      const key = `${item.question.trim().toLowerCase()}|${item.answer.trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [data]);

  useEffect(() => {
    if (!slug) {
      setError('Self-build slug is missing.');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getSelfBuildCostReport?slug=${encodeURIComponent(slug)}`
        );
        const result = await response.json();
        if (!result.success || !result.data) {
          setError('Self-build report not found.');
          return;
        }
        setData(result.data as SelfBuildCostReport2026);
      } catch (e) {
        setError('Unable to load self-build report.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  useEffect(() => {
    setActiveInsight('savings');
    setExpandedInsights({
      savings: false,
      procurement: false,
      risks: false,
    });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-3" />
        <p className="font-bold text-slate-500">Loading self-build report...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto my-20 px-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center">
          <CircleAlert className="h-16 w-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-3xl font-black text-slate-900 mb-3">Report Unavailable</h1>
          <p className="text-slate-600 mb-8">{error}</p>
          <Link
            to="/self-build"
            className="inline-block rounded-2xl bg-emerald-600 text-white px-8 py-4 font-black hover:bg-emerald-700 no-underline"
          >
            Open Self-Build Hub
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://rentsmart.ie/self-build/${data.slug}`;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const shareText = `Cost to build a ${data.bed_count}-bed house in ${data.county_name} in ${data.tax_year}. Check the full county estimate on RentSmart.ie.`;

  const costRows = [
    {
      label: 'Site Prep & Foundation',
      value: data.calculations.site_prep_and_foundation,
      color: 'border-sky-200 bg-sky-50',
    },
    {
      label: 'Build & Finishes',
      value: data.calculations.build_and_finishes,
      color: 'border-emerald-200 bg-emerald-50',
    },
    {
      label: 'Professional Fees',
      value: data.calculations.professional_fees,
      color: 'border-slate-200 bg-slate-50',
    },
    {
      label: 'Levies, VAT & Compliance',
      value: data.calculations.levies_vat_and_compliance,
      color: 'border-amber-200 bg-amber-50',
    },
    {
      label: 'Contingency',
      value: data.calculations.contingency,
      color: 'border-rose-200 bg-rose-50',
    },
  ];
  const insightGroups = [
    {
      key: 'savings' as const,
      title: 'Savings Moves',
      icon: Lightbulb,
      tone: {
        buttonActive: 'border-emerald-300 bg-emerald-50 text-emerald-900',
        buttonInactive: 'border-slate-200 bg-white text-slate-600 hover:border-emerald-200',
        card: 'border-emerald-200 bg-emerald-50',
        text: 'text-emerald-900',
      },
      items: data.ai_enrichment?.savings_moves || [],
    },
    {
      key: 'procurement' as const,
      title: 'Procurement Tips',
      icon: ShoppingCart,
      tone: {
        buttonActive: 'border-sky-300 bg-sky-50 text-sky-900',
        buttonInactive: 'border-slate-200 bg-white text-slate-600 hover:border-sky-200',
        card: 'border-sky-200 bg-sky-50',
        text: 'text-sky-900',
      },
      items: data.ai_enrichment?.procurement_tips || [],
    },
    {
      key: 'risks' as const,
      title: 'Build Risk Watchouts',
      icon: ShieldAlert,
      tone: {
        buttonActive: 'border-rose-300 bg-rose-50 text-rose-900',
        buttonInactive: 'border-slate-200 bg-white text-slate-600 hover:border-rose-200',
        card: 'border-rose-200 bg-rose-50',
        text: 'text-rose-900',
      },
      items: data.ai_enrichment?.build_risk_watchouts || [],
    },
  ];
  const currentInsight = insightGroups.find((group) => group.key === activeInsight) || insightGroups[0];
  const currentItems = currentInsight.items || [];
  const isExpanded = expandedInsights[currentInsight.key];
  const visibleItems = isExpanded ? currentItems : currentItems.slice(0, 3);
  const hasOverflow = currentItems.length > 3;

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
      <Helmet>
        <title>{data.seo_content.meta_title}</title>
        <meta name="description" content={data.seo_content.meta_desc} />
        <meta name="keywords" content={data.seo_content.keywords.join(', ')} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(data.schema)}</script>
      </Helmet>

      <header className="bg-slate-900 text-white pt-10 pb-20 px-4 relative overflow-hidden">
        <div className="absolute -top-20 -right-12 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />

        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-4">
                <Hammer className="h-4 w-4" /> Self-Build Cost Report
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-400/10 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-amber-200 mb-4">
                <AlertTriangle className="h-3.5 w-3.5" /> Estimated Planning Guide
              </div>
              <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-3">{data.seo_content.h1}</h1>
              <p className="text-slate-300 text-lg leading-relaxed max-w-3xl mb-5">
                {data.seo_content.snippet_hook}
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs font-black uppercase tracking-widest text-slate-400">Share:</span>
                <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
                  <FacebookIcon size={30} round />
                </FacebookShareButton>
                <TwitterShareButton url={shareUrl} title={shareText}>
                  <TwitterIcon size={30} round />
                </TwitterShareButton>
                <WhatsappShareButton url={shareUrl} title={shareText}>
                  <WhatsappIcon size={30} round />
                </WhatsappShareButton>
                <LinkedinShareButton
                  url={shareUrl}
                  title={`${data.county_name} Self-Build Cost ${data.tax_year}`}
                  summary={shareText}
                >
                  <LinkedinIcon size={30} round />
                </LinkedinShareButton>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="rounded-[2rem] border border-white/20 bg-white/10 backdrop-blur p-6">
                <p className="text-[10px] uppercase tracking-widest font-black text-slate-300 mb-1">
                  Estimated Total Build Cost
                </p>
                <p className="text-4xl font-black text-emerald-300 mb-3">
                  {formatCurrency(data.calculations.estimated_total_build_cost)}
                </p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-black text-slate-300 mb-1">
                      Cost per m2
                    </p>
                    <p className="font-black text-white">
                      {formatCurrency(data.calculations.estimated_cost_per_sqm)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-black text-slate-300 mb-1">
                      County Factor
                    </p>
                    <p className="font-black text-white">
                      {formatMultiplier(data.calculations.county_adjustment_factor)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 -mt-10 pb-20">
        <div className="grid lg:grid-cols-12 gap-8 mt-16">
          <section className="lg:col-span-8 space-y-8">
            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-9 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">Deterministic Cost Breakdown</h2>
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                {costRows.map((row) => (
                  <div key={row.label} className={`rounded-2xl border p-4 ${row.color}`}>
                    <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                      {row.label}
                    </p>
                    <p className="text-2xl font-black text-slate-900">{formatCurrency(row.value)}</p>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm font-semibold text-slate-700 leading-relaxed mb-3">
                {data.sections.benchmark_note}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm font-semibold text-slate-700 leading-relaxed mb-3">
                {data.sections.cost_breakdown_note}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm font-semibold text-slate-700 leading-relaxed">
                {data.sections.county_adjustment_note}
              </div>
            </div>

            {data.ai_enrichment ? (
              <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-9 shadow-sm">
                <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-emerald-600" /> Execution Intelligence
                </h2>
                <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-bold text-amber-900 mb-4">
                  Estimated guidance only: use this playbook to plan, then validate with local
                  quotes, planning constraints, and professional advice.
                </p>
                {data.ai_enrichment.cost_strategy_summary ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm font-semibold text-slate-700 leading-relaxed mb-3">
                    {data.ai_enrichment.cost_strategy_summary}
                  </div>
                ) : null}
                {data.ai_enrichment.county_execution_context ? (
                  <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5 text-sm font-semibold text-sky-900 leading-relaxed mb-5">
                    {data.ai_enrichment.county_execution_context}
                  </div>
                ) : null}
                <div className="grid md:grid-cols-3 gap-3 mb-4">
                  {insightGroups.map((group) => {
                    const Icon = group.icon;
                    const active = activeInsight === group.key;
                    return (
                      <button
                        key={group.key}
                        type="button"
                        onClick={() => setActiveInsight(group.key)}
                        className={`rounded-2xl border p-4 text-left transition-all ${active ? group.tone.buttonActive : group.tone.buttonInactive}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="inline-flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <p className="text-xs font-black uppercase tracking-wider">{group.title}</p>
                          </div>
                          <span className="text-[10px] font-black uppercase tracking-wider">
                            {group.items.length}
                          </span>
                        </div>
                        <p className="text-xs font-semibold opacity-80">
                          {group.items.length > 0 ? 'Open playbook' : 'No entries'}
                        </p>
                      </button>
                    );
                  })}
                </div>

                <div className={`rounded-2xl border p-5 ${currentInsight.tone.card}`}>
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <h3 className={`text-lg font-black ${currentInsight.tone.text}`}>
                      {currentInsight.title}
                    </h3>
                    <span className={`text-[10px] font-black uppercase tracking-wider ${currentInsight.tone.text}`}>
                      {currentItems.length} items
                    </span>
                  </div>

                  <ol className={`space-y-2 text-sm font-semibold ${currentInsight.tone.text}`}>
                    {visibleItems.map((item, index) => (
                      <li
                        key={`${currentInsight.key}-${item}-${index}`}
                        className="rounded-xl border border-white/50 bg-white/70 p-3"
                      >
                        <span className="mr-2 font-black">{index + 1}.</span>
                        {item}
                      </li>
                    ))}
                  </ol>

                  {hasOverflow ? (
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedInsights((prev) => ({
                          ...prev,
                          [currentInsight.key]: !prev[currentInsight.key],
                        }))
                      }
                      className={`mt-4 inline-flex items-center gap-1 rounded-xl border border-white/60 bg-white/80 px-3 py-2 text-xs font-black uppercase tracking-wider ${currentInsight.tone.text}`}
                    >
                      {isExpanded ? 'Show less' : `Show all ${currentItems.length}`}
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            <PseoEmailCapture
              pageType="self-build"
              pageSlug={data.slug}
              locationName={data.county_name}
              contextHeadline={`Track Self-Build Cost Changes In ${data.county_name}`}
              contextReason={`Get Irish market updates focused on self-build planning in ${data.county_name}, including cost trend changes, planning-risk signals, and new county-level estimate updates relevant to your role.`}
            />

            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-9 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">FAQ</h2>
              <div className="space-y-3">
                {mergedFaq.map((faq, index) => (
                  <details key={`${faq.question}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <summary className="cursor-pointer font-bold text-slate-800">{faq.question}</summary>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-amber-200 bg-amber-50 p-5">
              <p className="text-xs text-amber-900 font-semibold leading-relaxed">
                {data.sections.feasibility_note} This page is informational only and is not financial,
                legal, planning, or quantity-surveying advice.
              </p>
            </div>
          </section>

          <aside className="lg:col-span-4">
            <div className="sticky top-8 space-y-6">
              <div className="rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-white p-6 shadow-xl shadow-emerald-900/20">
                <h3 className="text-xl font-black mb-2">Monthly Mortgage-Equivalent</h3>
                <p className="text-4xl font-black text-emerald-200 mb-2">
                  {formatCurrency(data.calculations.monthly_mortgage_equivalent)}
                </p>
                <p className="text-emerald-100/90 text-sm mb-5 leading-relaxed">
                  Modeled at 90% financing over a 30-year term to give a fast affordability signal.
                </p>
                <a
                  href={data.ctas.rent.path}
                  className="no-underline flex items-start gap-2 justify-between rounded-xl bg-white text-emerald-800 px-4 py-3 font-black text-sm hover:bg-emerald-50 transition-all mb-2"
                >
                  <span>{data.ctas.rent.label}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 mt-0.5" />
                </a>
                <a
                  href={data.ctas.buy.path}
                  className="no-underline flex items-start gap-2 justify-between rounded-xl bg-emerald-500 text-white px-4 py-3 font-black text-sm hover:bg-emerald-600 transition-all"
                >
                  <span>{data.ctas.buy.label}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 mt-0.5" />
                </a>
              </div>

              {data.ctas.related.length ? (
                <div className="rounded-3xl border border-slate-200 bg-white p-6">
                  <h3 className="text-xl font-black text-slate-900 mb-3">Related Build Estimate</h3>
                  <div className="space-y-2">
                    {data.ctas.related.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="no-underline flex items-start gap-2 justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-700 hover:border-emerald-300 hover:text-emerald-700"
                      >
                        <span>{item.label}</span>
                        <ArrowRight className="h-4 w-4 shrink-0 mt-0.5" />
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-emerald-50 p-6">
                <h3 className="text-xl font-black text-slate-900 mb-2 flex items-center gap-2">
                  <Share2 className="h-5 w-5 text-emerald-600" /> Share RentSmart.ie
                </h3>
                <p className="text-sm text-slate-600 font-medium mb-4 leading-relaxed">
                  Share this county self-build estimate with friends planning their next move.
                </p>
                <div className="flex items-center gap-2">
                  <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
                    <FacebookIcon size={32} round />
                  </FacebookShareButton>
                  <TwitterShareButton url={shareUrl} title={shareText}>
                    <TwitterIcon size={32} round />
                  </TwitterShareButton>
                  <WhatsappShareButton url={shareUrl} title={shareText}>
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  <LinkedinShareButton
                    url={shareUrl}
                    title={`${data.county_name} Self-Build Cost ${data.tax_year}`}
                    summary={shareText}
                  >
                    <LinkedinIcon size={32} round />
                  </LinkedinShareButton>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-6">
                <Link
                  to="/self-build"
                  className="no-underline inline-flex items-center gap-2 text-sm font-black text-emerald-700 hover:text-emerald-800"
                >
                  Try another county or bed type <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </aside>
        </div>

        {data.calculations.estimated_total_build_cost <= 0 ? (
          <div className="mt-10 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 font-semibold">
            <AlertTriangle className="h-4 w-4 inline-block mr-2" />
            Build estimate is unavailable for this slug. Please return to the hub and choose another county.
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default SelfBuildCostPage;
